import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, withTenant, type PaginatedResult } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

/**
 * Canonical data hooks (frontend-patterns §6): every server read gates
 * on tokenReady + active tenant, keys are namespaced by tenant so a
 * tenant switch never shows stale cross-tenant cache, and mutations
 * invalidate by prefix.
 */

export function useTenantQuery<T>(
  key: readonly unknown[],
  path: string,
  params: Record<string, string | number | undefined> = {},
  options: { enabled?: boolean; refetchIntervalMs?: number } = {}
): UseQueryResult<T> {
  const { tokenReady } = useAuth();
  const { tenantId } = useTenant();
  return useQuery<T>({
    queryKey: ['tenant', tenantId, ...key],
    queryFn: () => api.get<T>(withTenant(path, tenantId as string, params)),
    enabled: tokenReady && tenantId !== null && (options.enabled ?? true),
    refetchInterval: options.refetchIntervalMs,
  });
}

export function useTenantPaginatedQuery<T>(
  key: readonly unknown[],
  path: string,
  params: Record<string, string | number | undefined> = {},
  options: { enabled?: boolean; refetchIntervalMs?: number } = {}
): UseQueryResult<PaginatedResult<T>> {
  const { tokenReady } = useAuth();
  const { tenantId } = useTenant();
  return useQuery<PaginatedResult<T>>({
    queryKey: ['tenant', tenantId, ...key, params],
    queryFn: () =>
      api.getPaginated<T>(withTenant(path, tenantId as string, params)),
    enabled: tokenReady && tenantId !== null && (options.enabled ?? true),
    refetchInterval: options.refetchIntervalMs,
  });
}

export function useTenantMutation<TInput, TOutput>(
  invalidatePrefix: readonly unknown[],
  mutator: (tenantId: string, input: TInput) => Promise<TOutput>
) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  return useMutation<TOutput, Error, TInput>({
    mutationFn: (input) => {
      if (!tenantId) throw new Error('No active tenant');
      return mutator(tenantId, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['tenant', tenantId, ...invalidatePrefix],
      });
    },
  });
}
